import { Box, Collapse, IconButton, AlertTitle } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Alert } from '../Alert';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { L10nHTML, useDownBreakpoint, useIsMobile } from '@uplift/hooks';
import { TipsAndUpdatesOutlinedIcon } from '@uplift/icons';
import { ExpandLess, ExpandMore } from '@mui/icons-material';

export type EditableSectionRowTipProps = {
  title: string;
  description: React.ReactNode;
};

const useEditableSectionRowTipStyles = makeStyles({ name: 'EditableSectionRowTip' })(theme => ({
  alertTitle: {
    marginBottom: 0,
    fontWeight: 700,
  },
  alertMessage: {
    padding: 0,
    width: '100%',
  },
  alertRoot: {
    padding: theme.spacing(2),
  },
  expandButton: {
    width: '24px',
    height: '24px',
  },
  fontSize14: {
    fontSize: '14px',
  }
}));

export const EditableSectionRowTip: React.VFC<EditableSectionRowTipProps> = props => {
  const isTabletOrMobile = useIsMobile('md');
  const [ open, setOpen ] = useState(false);
  const { classes } = useEditableSectionRowTipStyles();

  const description = useMemo(() => {
    if (typeof props.description === 'string') {
      return (
        <L10nHTML className={classes.fontSize14}>
          {props.description}
        </L10nHTML>
      );
    }

    return props.description;
  }, [ classes.fontSize14, props.description ]);

  const icon = (
    <Box color="white" bgcolor="info.main" display="flex" alignItems="center" justifyContent="center" borderRadius="50%" width="20px" height="20px" minWidth="20px">
      <TipsAndUpdatesOutlinedIcon color="inherit" className={classes.fontSize14} />
    </Box>
  );

  return (
    <Box width="100%">
      <Alert severity="info" showIcon={false} classes={{ message: classes.alertMessage, root: classes.alertRoot }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap="0.5rem">
          <Box display="flex" gap="0.5rem">
            {icon}
            <AlertTitle className={classes.alertTitle}>{props.title}</AlertTitle>
          </Box>
          <Box display={{ xs: 'flex', md: 'none' }}>
            <IconButton size="small" className={classes.expandButton} onClick={() => setOpen(p => !p)}>
              {open ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
        </Box>
        <Collapse in={open || !isTabletOrMobile} timeout={{ enter: 300, exit: 0 }}>
          <Box mt={0.5}>
            {description}
          </Box>
        </Collapse>
      </Alert>
    </Box>
  );
};

type EditableSectionTipWidthProps = {
  setTipWidth: React.Dispatch<React.SetStateAction<number>>;
};


export const EditableSectionTipWidth: React.VFC<EditableSectionTipWidthProps> = ({ setTipWidth }) => {
  const isMdDown = useDownBreakpoint('md');

  if (isMdDown) return null;

  return (
    <MountForTabletAndDesktop setTipWidth={setTipWidth} />
  );
};

const MountForTabletAndDesktop: React.VFC<EditableSectionTipWidthProps> = ({ setTipWidth }) => {
  const measureRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!measureRef.current) return;

    const resizeObserver = new ResizeObserver(entries => {
      setTipWidth(entries[0].contentRect.width);
    });

    resizeObserver.observe(measureRef.current);
    return () => resizeObserver.disconnect();
  }, [ setTipWidth ]);

  return (
    <Box flex={1} minWidth="176px" ml="24px">
      <div ref={measureRef} style={{ width: '100%' }} />
    </Box>
  );
};
